<template>
  <footer class="app-footer" :class="{ 'in-game': isGameView }">
    <!-- Logged-Out Footer (Marketing) -->
    <div v-if="!user" class="footer-content">
      <div class="social-links">
        <a
          href="https://bsky.app/profile/baltindors.bsky.social"
          target="_blank"
          rel="noopener noreferrer"
          >Bluesky</a
        >
        <button disabled>Discord</button>
        <a
          href="https://ko-fi.com/celestiarpg"
          target="_blank"
          rel="noopener noreferrer"
          >Donate</a
        >
      </div>
      <div class="copyright">
        &copy; {{ new Date().getFullYear() }} Celestia RPG. All rights reserved.
      </div>
    </div>

    <!-- Logged-In Footer (In-Game) -->
    <nav v-else class="footer-nav">
      <router-link
        v-for="item in navItems"
        :key="item.name"
        :to="item.path"
        class="nav-item"
        @click="playSound('futuristic-click.mp3')"
      >
        <i :class="item.icon"></i>
        <span>{{ item.name }}</span>
      </router-link>
    </nav>
  </footer>
</template>

<script setup>
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import { useAuthStore } from "../store/auth";
import { playSound } from "../utils/audio";

const authStore = useAuthStore();
const route = useRoute();
const user = computed(() => authStore.user);

const isGameView = computed(() => route.meta.isGame);

const navItems = ref([
  { name: "Home", path: "/game/home", icon: "fas fa-dice-d20" },
  { name: "City", path: "/game/city", icon: "fas fa-dungeon" },
  { name: "Hero", path: "/game/hero", icon: "fas fa-user-shield" },
  { name: "Cards", path: "/game/cards", icon: "fas fa-clone" },
  { name: "Quest", path: "/game/quest", icon: "fas fa-scroll" },
  { name: "Battle", path: "/game/battle", icon: "fas fa-fist-raised" },
  { name: "World", path: "/game/world", icon: "fas fa-globe" },
  { name: "Guild", path: "/game/guild", icon: "fas fa-landmark" },
  { name: "Trade", path: "/game/trade", icon: "fas fa-exchange-alt" },
]);
</script>

<style scoped>
@import url("https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css");

/* --- Default styles for the MARKETING footer --- */
.app-footer {
  background-color: rgba(10, 10, 20, 0.8);
  border-top: 1px solid rgba(0, 246, 255, 0.5);
  padding: 2rem;
  text-align: center;
  color: #c0c0c0;
  width: 100%;
  box-sizing: border-box;
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.social-links {
  display: flex;
  justify-content: center;
  gap: 1rem;
  align-items: center;
}

.social-links a,
.social-links button {
  font-family: "Orbitron", sans-serif;
  background: transparent;
  color: #00f6ff;
  border: 1px solid #00f6ff;
  border-radius: 5px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  text-decoration: none;
  transition: all 0.3s ease-in-out;
}

.social-links a:hover {
  background-color: rgba(0, 246, 255, 0.2);
  box-shadow: 0 0 15px rgba(0, 246, 255, 0.5);
}

.social-links button:disabled {
  border-color: #555;
  color: #555;
  cursor: not-allowed;
}

/* --- Styles specifically for the IN-GAME footer --- */
/* These styles ONLY apply when the .in-game class is present */
.app-footer.in-game {
  position: fixed;
  bottom: 0;
  left: 0;
  height: 80px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  z-index: 1000;
}

.footer-nav {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem; /* Adjusted gap for better spacing */
  width: 100%;
  height: 100%;
  padding: 0 2rem;
  box-sizing: border-box;
}

.nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  color: #e0e0e0;
  text-decoration: none;
  font-family: "Exo 2", sans-serif;
  font-size: 0.85rem; /* Slightly smaller text */
  transition: color 0.3s ease, text-shadow 0.3s ease;
}

.nav-item i {
  font-size: 1.6rem; /* Slightly smaller icons */
}

.nav-item:hover {
  color: #00f6ff;
  text-shadow: 0 0 10px #00f6ff;
}

/* Style for the active link */
.nav-item.router-link-exact-active {
  color: #ff00ff; /* A vibrant color for the active link */
  text-shadow: 0 0 10px #ff00ff;
}

@media (max-width: 900px) {
  .footer-nav {
    gap: 0.35rem;
    padding: 0 0.35rem;
    overflow-x: auto;
    justify-content: flex-start;
  }
  .nav-item {
    flex: 1 0 auto;
    min-width: 3.25rem;
    gap: 0.2rem;
    font-size: 0.7rem;
  }
  .nav-item i {
    font-size: 1.25rem;
  }
}
</style>
